import React from "react";
import {
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Grid,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import { ColumnCenteredBox } from "../../../../utils/styledBox";

interface ImageAnalysisTabProps {
  uploadedImage: string | null;
  analyzeLoading: boolean;
  isAnyLoading: boolean;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onImageDelete: () => void;
  onImageClick: () => void;
  onAnalyzeImage: () => void;
  onGeneratedImageModalOpen: () => void;
}

const ImageAnalysisTab: React.FC<ImageAnalysisTabProps> = ({
  uploadedImage,
  analyzeLoading,
  isAnyLoading,
  onImageUpload,
  onImageDelete,
  onImageClick,
  onAnalyzeImage,
  onGeneratedImageModalOpen,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Image Upload Section */}
      <Grid size={{ xs: 12 }}>
        <ColumnCenteredBox>
          <Box display="flex" gap={2} alignItems="center">
            <Box
              sx={{
                display: "flex",
                width: 120,
                height: 120,
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
                outline: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                borderRadius: 1,
                cursor: "pointer",
                position: "relative",
              }}
              onClick={onImageClick}
            >
              {uploadedImage ? (
                <>
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 4 }}
                  />
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backdropFilter: "blur(5px)",
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                      },
                    }}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onImageDelete();
                    }}
                  >
                    <DeleteIcon style={{ color: "white" }} fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CloudUploadIcon fontSize="large" sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {t("juno.enhancement.image.upload")}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={onImageClick}
                size="small"
                disabled={isAnyLoading}
              >
                {t("juno.enhancement.image.upload")}
              </Button>
              <Button
                variant="outlined"
                startIcon={<AddPhotoAlternateIcon />}
                onClick={onGeneratedImageModalOpen}
                size="small"
                disabled={isAnyLoading}
              >
                {t("juno.input.imageEditor.generated")}
              </Button>
            </Box>
          </Box>
        </ColumnCenteredBox>
      </Grid>

      {/* Image Action Buttons */}
      <Grid size={{ xs: 12 }}>
        <Box display="flex" gap={2} justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="warning">
            {t("juno.enhancement.creditNotice")}
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={
                analyzeLoading ? <CircularProgress size={16} color="inherit" /> : <ImageSearchIcon />
              }
              onClick={onAnalyzeImage}
              disabled={!uploadedImage || isAnyLoading}
              sx={{ minWidth: 160 }}
            >
              {t("juno.enhancement.image.analyze")}
            </Button>
          </Box>
        </Box>
      </Grid>
    </>
  );
};

export default ImageAnalysisTab;